import { Box, Stack, TextField } from '@atgfr8/atg-ui-tailwind';
import Button from '@components/Button/Button';
import { BasicModal } from '@components/Modal/BasicModal';
import { SelectInput } from '@components/Select/SelectInput';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import {
  createCollectionFlagAction,
  deleteCollectionFlagAction,
  updateCollectionFlagAction,
} from '@store/collectionFlags/actions';
import {
  collectionFlagStore,
  setCollectionFlagStore,
} from '@store/collectionFlags/store';
import type {
  CollectionFlag,
  CollectionFlagChangeTrackingSources,
  CollectionFlagModalMode,
  CollectionFlagTypeName,
} from '@store/collectionFlags/types';
import { closeModal, openModal } from '@store/modals';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { Show } from 'solid-js';
import * as yup from 'yup';

import {
  COLLECTION_FLAG_MODAL_ID,
  COLLECTION_FLAG_TYPE_OPTIONS,
  findCollectionFlagType,
} from './constants';

type CollectionFlagFormValues = {
  flagType: string;
  description: string;
};

type CollectionFlagModalProps = {
  customerId: number;
  changeTrackingSources: CollectionFlagChangeTrackingSources;
};

const collectionFlagSchema = yup.object({
  flagType: yup.string().required('Flag Type is required'),
  description: yup.string(),
});

const firstError = (error?: string | string[] | null): string | undefined => {
  if (!error) return undefined;
  return Array.isArray(error) ? error.join(', ') : String(error);
};

export const openCollectionFlagModal = (
  mode: CollectionFlagModalMode,
  flag: CollectionFlag | null = null,
) => {
  setCollectionFlagStore({ modalMode: mode, selectedFlag: flag });
  openModal(COLLECTION_FLAG_MODAL_ID);
};

const isEditMode = () => collectionFlagStore.modalMode === 'edit';

const isDeleting = () => collectionFlagStore.deletingFlagId !== null;

const getInitialValues = (): CollectionFlagFormValues => {
  const flag = collectionFlagStore.selectedFlag;
  if (!isEditMode() || !flag) {
    return { flagType: '', description: '' };
  }
  return {
    flagType: findCollectionFlagType(flag.flagType)?.value ?? '',
    description: flag.description ?? '',
  };
};

const CollectionFlagForm = (props: CollectionFlagModalProps) => {
  const { data, setData, form, errors, isSubmitting, setIsSubmitting } =
    createForm<CollectionFlagFormValues>({
      initialValues: getInitialValues(),
      extend: validator({ schema: collectionFlagSchema }),
      onSubmit: async (values) => {
        try {
          setIsSubmitting(true);
          const payload = {
            customerId: props.customerId,
            flagType: values.flagType as CollectionFlagTypeName,
            description: values.description.trim(),
          };
          const selectedFlag = collectionFlagStore.selectedFlag;
          const isSuccess =
            isEditMode() && selectedFlag
              ? await updateCollectionFlagAction(
                  {
                    ...payload,
                    id: selectedFlag.id,
                    operationType: 'Update',
                  },
                  props.changeTrackingSources.update,
                )
              : await createCollectionFlagAction(
                  { ...payload, operationType: 'Insert' },
                  props.changeTrackingSources.create,
                );
          if (isSuccess) {
            closeModal(COLLECTION_FLAG_MODAL_ID);
          }
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const isBusy = () => isSubmitting() || isDeleting();

  return (
    <form ref={form}>
      <Stack spacing={1.5} width={900} maxWidth={'90vw'}>
        <Box display="flex" gap={1.5} alignItems="flex-start" mt={2}>
          <SelectInput
            required
            label="Flag Type"
            placeholder="Select from dropdown"
            menuItems={COLLECTION_FLAG_TYPE_OPTIONS}
            value={data().flagType}
            onChange={(value) => setData('flagType', value)}
            error={firstError(errors().flagType)}
            dataTestId={formatDataTestId('Collection Flag Type', 'selectInput')}
          />
        </Box>
        <TextField
          label="Description"
          multiline
          rows={4}
          value={data().description}
          onChange={(value: string) => setData('description', value)}
          error={firstError(errors().description)}
          dataTestId="collection-flag-description-textInput"
        />
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button
            disabled={isBusy()}
            isLoading={isSubmitting()}
            type="submit"
            label={isEditMode() ? 'Update Flag' : 'Save'}
            variant="contained"
            dataTestId={
              isEditMode()
                ? 'collection-flag-update-button'
                : 'collection-flag-save-button'
            }
          />
          <Show
            when={isEditMode() && collectionFlagStore.selectedFlag?.canDelete}
          >
            <Button
              type="button"
              disabled={isBusy()}
              isLoading={isDeleting()}
              label="Remove Flag"
              variant="contained"
              class="bg-[#B00020]!"
              dataTestId="collection-flag-remove-button"
              onClick={async () => {
                const flag = collectionFlagStore.selectedFlag;
                if (!flag) return;
                const isSuccess = await deleteCollectionFlagAction(
                  flag.id,
                  props.changeTrackingSources.delete,
                );
                if (isSuccess) {
                  closeModal(COLLECTION_FLAG_MODAL_ID);
                }
              }}
            />
          </Show>
          <Button
            type="button"
            label="Cancel"
            variant="text"
            disabled={isBusy()}
            onClick={() => closeModal(COLLECTION_FLAG_MODAL_ID)}
            dataTestId="collection-flag-cancel-button"
          />
        </Box>
      </Stack>
    </form>
  );
};

export const CollectionFlagModal = (props: CollectionFlagModalProps) => (
  <BasicModal
    id={COLLECTION_FLAG_MODAL_ID}
    header={true}
    class="w-full max-w-237!"
    title={isEditMode() ? 'Edit Flag' : 'Add Flag'}
    footer={false}
    showClose={false}
  >
    <CollectionFlagForm
      customerId={props.customerId}
      changeTrackingSources={props.changeTrackingSources}
    />
  </BasicModal>
);
